import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";

import CloseIcon from "@/assets/icons/admin/close.svg?react";
import CopyIcon from "@/assets/icons/admin/copy.svg?react";
import MailIcon from "@/assets/icons/admin/mail.svg?react";
import TrashIcon from "@/assets/icons/admin/trash.svg?react";
import { cn } from "@/lib/utils";

export type AdminLeadBreakdown = {
  labourWaste: string;
  yieldPenalty: string;
  untappedValue: string;
  totalTrappedMargin: string;
};

export type AdminLeadDetail = {
  id: string;
  sessionId: string;
  relativeTime: string;
  email: string;
  organisationName: string;
  region: string;
  regionName: string;
  sector: string;
  targetEbitda: string;
  tciScore: number;
  scoreTone: "critical" | "warning";
  breakdown: AdminLeadBreakdown;
};

type AdminLeadDetailModalProps = {
  lead: AdminLeadDetail | null;
  onClose: () => void;
  onDelete?: (lead: AdminLeadDetail) => void;
  onSendMail?: (lead: AdminLeadDetail) => void;
};

function scoreTextClass(tone: AdminLeadDetail["scoreTone"]): string {
  return tone === "critical" ? "text-[#ef4444]" : "text-[#eab308]";
}

export function AdminLeadDetailModal({
  lead,
  onClose,
  onDelete,
  onSendMail,
}: AdminLeadDetailModalProps) {
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!lead) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        onClose();
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    dialogRef.current?.focus();

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [lead, onClose]);

  useEffect(() => {
    setCopied(false);
  }, [lead?.sessionId]);

  if (!lead) return null;

  const email = lead.email;

  async function handleCopyEmail() {
    try {
      await navigator.clipboard.writeText(email);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-[4.2px]"
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="w-full max-w-[550px] overflow-hidden rounded-xl border border-white/5 bg-[#132136] outline-none"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between px-6 pb-2 pt-6">
          <div className="flex flex-col gap-1">
            <p id={titleId} className="text-sm font-bold text-white">
              {lead.sessionId}
            </p>
            <p className="text-xs text-[#9194a1]">{lead.relativeTime}</p>
          </div>
          <button
            type="button"
            aria-label="Close lead details"
            className="flex size-6 shrink-0 items-center justify-center rounded-full bg-[#9194a1]"
            onClick={onClose}
          >
            <CloseIcon aria-hidden className="size-3.5" />
          </button>
        </div>

        <div className="flex items-center justify-between gap-4 px-6 pb-4 pt-1">
          <div className="flex min-w-0 flex-col">
            <div className="flex items-center gap-0.5">
              <p className="truncate text-base text-[#9194a1]">{lead.email}</p>
              <button
                type="button"
                aria-label={copied ? "Email copied" : "Copy email address"}
                className="shrink-0 rounded p-1 hover:bg-white/5"
                onClick={handleCopyEmail}
              >
                <CopyIcon aria-hidden className="size-4 text-[#38BDF8]" />
              </button>
              {copied ? (
                <span className="sr-only" aria-live="polite">
                  Copied
                </span>
              ) : null}
            </div>
            <p className="overflow-hidden truncate text-ellipsis text-[16px] text-sm font-medium leading-5 text-white">
              {lead.organisationName}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2 text-base">
            <span className="text-[#6b7280]">TCI SCORE:</span>
            <span
              className={cn("font-extrabold", scoreTextClass(lead.scoreTone))}
            >
              {lead.tciScore}%
            </span>
          </div>
        </div>

        <div className="flex flex-col gap-5 px-6 pb-6">
          <div className="flex h-[58px] items-center justify-between rounded-md bg-[#1d2e46] p-4">
            <p className="text-xs font-bold uppercase tracking-[0.5px] text-[#6b7280]">
              Target Annual Profit (EBITDA)
            </p>
            <p className="text-base font-extrabold text-white">
              {lead.targetEbitda}
            </p>
          </div>

          <div className="flex flex-col gap-4">
            {[
              { label: "Labour Waste", value: lead.breakdown.labourWaste },
              { label: "Yield Penalty", value: lead.breakdown.yieldPenalty },
              { label: "Untapped Value", value: lead.breakdown.untappedValue },
            ].map((item, index) => (
              <div
                key={item.label}
                className="flex h-[58px] flex-col justify-center rounded-md border border-white/5 bg-[#0c1b2e] px-3 py-4"
              >
                <div className="flex items-center justify-between text-sm font-bold">
                  <ol className="list-decimal text-white" start={index + 1}>
                    <li className="ms-[21px]">{item.label}</li>
                  </ol>
                  <p className="text-[#9194a1]">{item.value}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="flex h-[58px] items-center rounded-md border border-[rgba(239,68,68,0.2)] bg-[rgba(239,68,68,0.05)] p-4">
            <div className="flex w-full items-center justify-between text-sm font-bold text-[#ef4444]">
              <p>Total Trapped Margin</p>
              <p>{lead.breakdown.totalTrappedMargin}</p>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between px-6 pb-6 pt-1 text-sm">
          <div className="flex flex-col gap-0.5">
            <p className="text-[#9194a1]">Region</p>
            <p className="font-bold text-white">{lead.regionName}</p>
          </div>
          <div className="flex flex-col items-end gap-0.5">
            <p className="text-[#6b7280]">Sector</p>
            <p className="font-bold text-white">{lead.sector}</p>
          </div>
        </div>

        <div className="flex gap-4 px-6 pb-6 pt-3">
          <button
            type="button"
            className="flex h-12 flex-1 items-center justify-center gap-2 rounded-md border border-[rgba(145,148,161,0.2)] text-sm font-bold text-[#ef4444] transition-colors hover:bg-white/5"
            onClick={() => onDelete?.(lead)}
          >
            <TrashIcon aria-hidden className="size-4" />
            Delete Lead
          </button>
          <button
            type="button"
            className="flex h-12 flex-1 items-center justify-center gap-2 rounded-md bg-[#38bdf8] text-sm font-bold text-white transition-colors hover:bg-[#0ea5e9]"
            onClick={() => onSendMail?.(lead)}
          >
            <MailIcon aria-hidden className="size-4" />
            Send Mail
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
